import { api } from "@/lib/api/client";
import type {
  Task,
  AdjustmentTask,
  TasksResponse,
  TaskFilters,
  CreateTaskDto,
  UpdateTaskDto,
  CompleteAdjustmentDto,
  TaskType,
  TaskTypesResponse,
  CreateTaskTypeDto,
  UpdateTaskTypeDto,
} from "../types";

// Query keys for new-tasks (separate from original tasks)
export const newTaskKeys = {
  all: ["new-tasks"] as const,
  lists: () => [...newTaskKeys.all, "list"] as const,
  list: (filters: TaskFilters) => [...newTaskKeys.lists(), filters] as const,
  details: () => [...newTaskKeys.all, "detail"] as const,
  detail: (id: string | number) => [...newTaskKeys.details(), id] as const,
  statusCount: (filters?: TaskFilters) => [...newTaskKeys.all, "status-count", filters] as const,
  adjustments: (id: string | number) => [...newTaskKeys.all, "adjustments", id] as const,
};

// Query keys for task types (shared with original)
export const newTaskTypeKeys = {
  all: ["new-task-types"] as const,
  lists: () => [...newTaskTypeKeys.all, "list"] as const,
  list: (filters?: Record<string, unknown>) =>
    [...newTaskTypeKeys.lists(), filters] as const,
  details: () => [...newTaskTypeKeys.all, "detail"] as const,
  detail: (id: string | number) => [...newTaskTypeKeys.details(), id] as const,
};

// Build query params from filters
const buildTaskParams = (filters: TaskFilters): Record<string, unknown> => {
  const offset = filters.page * filters.limit;
  const params: Record<string, unknown> = {
    limit: filters.limit,
    offset,
  };

  // Search
  if (filters.s && filters.s.length >= 2) {
    params["s"] = filters.s;
  }

  // Sorting
  if (filters.orderBy && filters.order) {
    params["orderBy"] = filters.orderBy;
    params["order"] = filters.order;
  }

  // Status — backend uses newStatus (array) query param. An exact-id lookup
  // (e.g. a notification deep-link `?id=`) skips it: the task must be found
  // whatever stage it is in.
  if (!filters.id) {
    if (filters.statuses && filters.statuses.length > 0) {
      params["newStatus"] = filters.statuses;
    } else if (filters.status) {
      params["newStatus"] = [filters.status];
    }
  }

  // Priority
  if (filters.priority) {
    params["priority"] = filters.priority;
  }

  // Date filters
  if (filters["createdAt.min"]) {
    const minDate = new Date(filters["createdAt.min"]);
    minDate.setHours(0, 0, 0, 0);
    params["createdAt.min"] = minDate.toISOString();
  }

  if (filters["createdAt.max"]) {
    const maxDate = new Date(filters["createdAt.max"]);
    maxDate.setHours(23, 59, 59, 999);
    params["createdAt.max"] = maxDate.toISOString();
  }

  if (filters["dueAt.min"]) {
    params["dueAt.min"] = new Date(filters["dueAt.min"]).toISOString();
  }

  if (filters["dueAt.max"]) {
    params["dueAt.max"] = new Date(filters["dueAt.max"]).toISOString();
  }

  // ID filters
  if (filters.id) params["id"] = filters.id;
  if (filters.periodId) params["periodId"] = filters.periodId;
  if (filters.clientId) params["clientId"] = filters.clientId;
  if (filters.createdById) params["createdById"] = filters.createdById;
  if (filters.matterId) params["matterId"] = filters.matterId;
  if (filters.assignedStaffId)
    params["assignedStaffId"] = filters.assignedStaffId;
  if (filters.typeId) params["typeId"] = filters.typeId;

  // parentId: null = root tasks only, number = subtasks of specific parent
  if (filters.parentId === null) {
    params["parentId"] = "null";
  } else if (filters.parentId) {
    params["parentId"] = filters.parentId;
  }

  // Adjustment visibility params
  if (filters.excludeAdjustments !== undefined) {
    params["excludeAdjustments"] = filters.excludeAdjustments;
  }
  if (filters.viewerUserId !== undefined) {
    params["viewerUserId"] = filters.viewerUserId;
  }

  // Review-board scoping params
  if (filters.responsibleAttorneyId !== undefined) {
    params["responsibleAttorneyId"] = filters.responsibleAttorneyId;
  }
  if (filters.partnerId !== undefined) {
    params["partnerId"] = filters.partnerId;
  }
  if (filters.partnerReviewUserId !== undefined) {
    params["partnerReviewUserId"] = filters.partnerReviewUserId;
  }
  if (filters.myTasksUserId !== undefined) {
    params["myTasksUserId"] = filters.myTasksUserId;
  }
  if (filters.excludeAssignedStaffId !== undefined) {
    params["excludeAssignedStaffId"] = filters.excludeAssignedStaffId;
  }

  return params;
};

// Build query params for status-count (same filters as tasks, but no pagination/sorting/status)
const buildStatusCountParams = (filters: TaskFilters): Record<string, unknown> => {
  const params: Record<string, unknown> = {};

  if (filters.s && filters.s.length >= 2) params["s"] = filters.s;
  if (filters.priority) params["priority"] = filters.priority;

  if (filters["createdAt.min"]) {
    const minDate = new Date(filters["createdAt.min"]);
    minDate.setHours(0, 0, 0, 0);
    params["createdAt.min"] = minDate.toISOString();
  }
  if (filters["createdAt.max"]) {
    const maxDate = new Date(filters["createdAt.max"]);
    maxDate.setHours(23, 59, 59, 999);
    params["createdAt.max"] = maxDate.toISOString();
  }
  if (filters["dueAt.min"]) params["dueAt.min"] = new Date(filters["dueAt.min"]).toISOString();
  if (filters["dueAt.max"]) params["dueAt.max"] = new Date(filters["dueAt.max"]).toISOString();

  if (filters.id) params["id"] = filters.id;
  if (filters.periodId) params["periodId"] = filters.periodId;
  if (filters.clientId) params["clientId"] = filters.clientId;
  if (filters.createdById) params["createdById"] = filters.createdById;
  if (filters.matterId) params["matterId"] = filters.matterId;
  if (filters.assignedStaffId) params["assignedStaffId"] = filters.assignedStaffId;
  if (filters.typeId) params["typeId"] = filters.typeId;

  if (filters.parentId === null) {
    params["parentId"] = "null";
  } else if (filters.parentId) {
    params["parentId"] = filters.parentId;
  }

  if (filters.excludeAdjustments !== undefined) {
    params["excludeAdjustments"] = filters.excludeAdjustments;
  }
  if (filters.viewerUserId !== undefined) {
    params["viewerUserId"] = filters.viewerUserId;
  }
  if (filters.responsibleAttorneyId !== undefined) {
    params["responsibleAttorneyId"] = filters.responsibleAttorneyId;
  }
  if (filters.partnerId !== undefined) {
    params["partnerId"] = filters.partnerId;
  }
  if (filters.partnerReviewUserId !== undefined) {
    params["partnerReviewUserId"] = filters.partnerReviewUserId;
  }
  if (filters.myTasksUserId !== undefined) {
    params["myTasksUserId"] = filters.myTasksUserId;
  }
  if (filters.excludeAssignedStaffId !== undefined) {
    params["excludeAssignedStaffId"] = filters.excludeAssignedStaffId;
  }

  return params;
};

// Task API functions
export const newTasksApi = {
  // Get all tasks with filters
  getAll: async (filters: TaskFilters): Promise<TasksResponse> => {
    const params = buildTaskParams(filters);
    return api.get<TasksResponse>("/tasks", { params });
  },

  // Get single task
  getById: async (id: string | number): Promise<Task> => {
    return api.get<Task>(`/tasks/${id}`);
  },

  // Create task
  create: async (data: CreateTaskDto): Promise<Task> => {
    return api.post<Task>("/tasks", data);
  },

  // Update task
  update: async (id: string | number, data: UpdateTaskDto): Promise<Task> => {
    return api.patch<Task>(`/tasks/${id}`, data);
  },

  // Delete task
  delete: async (id: string | number): Promise<void> => {
    await api.delete(`/tasks/${id}`);
  },

  // Get task counts grouped by newStatus
  getStatusCount: async (filters: TaskFilters): Promise<{ newStatus: string; count: number }[]> => {
    const params = buildStatusCountParams(filters);
    return api.get<{ newStatus: string; count: number }[]>("/tasks/status-count", { params });
  },

  // Change task status via backend change-status endpoint
  changeStatus: async (
    id: string | number,
    data: {
      newStatus: string;
      comment?: string;
      result?: string;
      isChangeRequest?: boolean;
      dueAt?: string;
    },
  ): Promise<Task> => {
    return api.patch<Task>(`/tasks/change-status/${id}`, data);
  },

  // Create an adjustment child task — no body (percent is recorded later via complete)
  adjust: async (id: string | number): Promise<Task> => {
    return api.post<Task>(`/tasks/adjustment/${id}`, {});
  },

  // Finish an adjustment task: record adjustmentPercent + optional comment
  completeAdjustment: async (
    adjustmentId: string | number,
    data: CompleteAdjustmentDto,
  ): Promise<Task> => {
    return api.patch<Task>(`/tasks/adjustment/${adjustmentId}/complete`, data);
  },

  // Fetch adjustment children of an original task
  getAdjustments: async (id: string | number): Promise<AdjustmentTask[]> => {
    return api.get<AdjustmentTask[]>(`/tasks/${id}/adjustments`);
  },

  // Soft-delete an adjustment task — auth mirrors `adjust` (matter-membership matrix)
  deleteAdjustment: async (
    adjustmentId: string | number,
  ): Promise<{ id: number; deletedAt: string }> => {
    return api.delete<{ id: number; deletedAt: string }>(
      `/tasks/adjustment/${adjustmentId}`,
    );
  },

  // Accept task
  acceptTask: async (
    id: string | number,
    data?: { billableDurationInSeconds?: number; durationInSeconds?: number },
  ): Promise<Task> => {
    return api.patch<Task>(`/tasks/accept/${id}`, data ?? {});
  },

  // Reassign task's assigned staff
  reassignStaff: async (
    id: string | number,
    staffId: number,
  ): Promise<Task> => {
    return api.patch<Task>(`/tasks/reassign-staff/${id}`, { staffId });
  },

  // Get task durations summed across the chain (original + live adjustments):
  // totalDuration = all activities; billableDuration = ACTIVE activities only.
  getDurations: async (
    id: string | number,
  ): Promise<{ totalDuration: number; billableDuration: number }> => {
    return api.get<{ totalDuration: number; billableDuration: number }>(
      `/tasks/${id}/durations`,
    );
  },
};

// Task Types API functions
export const newTaskTypesApi = {
  // Get all task types
  getAll: async (
    params?: Record<string, unknown>,
  ): Promise<TaskTypesResponse> => {
    return api.get<TaskTypesResponse>("/task-types", { params });
  },

  // Get single task type
  getById: async (id: string | number): Promise<TaskType> => {
    return api.get<TaskType>(`/task-types/${id}`);
  },

  // Create task type
  create: async (data: CreateTaskTypeDto): Promise<TaskType> => {
    return api.post<TaskType>("/task-types", data);
  },

  // Update task type
  update: async (
    id: string | number,
    data: UpdateTaskTypeDto,
  ): Promise<TaskType> => {
    return api.patch<TaskType>(`/task-types/${id}`, data);
  },

  // Delete task type
  delete: async (id: string | number): Promise<void> => {
    await api.delete(`/task-types/${id}`);
  },
};
